
import React, { useState, useEffect } from 'react';
import { X, Save, Mail, Lock, Shield, Loader2, AlertCircle, ToggleLeft, Calendar, FolderPlus } from 'lucide-react';
import { User as UserType } from '../types';

interface AdminUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (user: UserType) => Promise<void>;
  userToEdit?: UserType | null;
  theme: 'dark' | 'light';
  existingUsers?: UserType[];
}

const getEcuadorTime = () => {
  const now = new Date();
  const offset = -5; // Ecuador is UTC-5
  const ecuadorTime = new Date(now.getTime() + (offset * 60 * 60 * 1000));
  return ecuadorTime.toISOString().slice(0, 16);
};

const AdminUserModal: React.FC<AdminUserModalProps> = ({ isOpen, onClose, onSave, userToEdit, theme, existingUsers = [] }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('USUARIO');
  const [status, setStatus] = useState('ACTIVO');
  const [maxPortfolios, setMaxPortfolios] = useState<number>(1);
  const [date, setDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      if (userToEdit) {
        setUsername(userToEdit.username);
        setPassword(userToEdit.password);
        setRole(userToEdit.role);
        setStatus(userToEdit.status || 'ACTIVO');
        setMaxPortfolios(userToEdit.maxPortfolios || 1);
        let initialDate = userToEdit.date;
        if (initialDate && initialDate.length > 16) initialDate = initialDate.slice(0, 16);
        setDate(initialDate || getEcuadorTime());
      } else {
        setUsername('');
        setPassword('');
        setRole('USUARIO');
        setStatus('ACTIVO');
        setMaxPortfolios(1);
        setDate(getEcuadorTime());
      }
      setIsSubmitting(false);
    }
  }, [isOpen, userToEdit]);

  if (!isOpen) return null;

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!username || !password || !role) return;
    if (!validateEmail(username)) { setError('Correo electrónico inválido.'); return; }

    const isDuplicate = existingUsers.some(u => 
      u.username.trim().toLowerCase() === username.trim().toLowerCase() && 
      u.id !== (userToEdit ? userToEdit.id : '')
    );
    if (isDuplicate) { setError('Este correo electrónico ya está registrado.'); return; }

    setIsSubmitting(true);
    const userData: UserType = {
      id: userToEdit ? userToEdit.id : `user-${Date.now()}`,
      username: username.trim(),
      password,
      role,
      status,
      date,
      maxPortfolios: Math.max(1, maxPortfolios)
    };
    try {
      await onSave(userData);
      onClose();
    } catch (error) { setError("Error al guardar el usuario."); } 
    finally { setIsSubmitting(false); }
  };

  const themeClasses = {
    overlay: "fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300",
    container: `${theme === 'dark' ? 'bg-[#1a1c22] border-[#2d3139]' : 'bg-white border-slate-200'} border w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 custom-scrollbar`,
    header: `flex items-center justify-between p-5 border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} sticky top-0 bg-inherit z-10`,
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    inputBg: theme === 'dark' ? 'bg-black/20' : 'bg-slate-50',
    inputBorder: theme === 'dark' ? 'border-white/10' : 'border-slate-200',
    label: "block text-[10px] font-black uppercase tracking-[0.1em] mb-1.5 opacity-70"
  };

  return (
    <div className={themeClasses.overlay}>
      <div className={themeClasses.container}>
        <div className={themeClasses.header}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}><Mail size={20} /></div>
            <div>
              <h2 className={`text-lg font-black tracking-tight ${themeClasses.textMain}`}>{userToEdit ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <p className="text-[10px] uppercase font-bold text-slate-500">Gestión de Acceso</p>
            </div>
          </div>
          <button onClick={onClose} disabled={isSubmitting} className="p-2 rounded-full transition-all"><X size={18} className={themeClasses.textMain} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={`${themeClasses.label} ${themeClasses.textMain}`}>Correo electrónico</label>
            <div className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl border ${themeClasses.inputBg} ${themeClasses.inputBorder}`}>
              <Mail size={16} className="text-slate-500" />
              <input type="email" value={username} onChange={(e) => { setUsername(e.target.value); if (error) setError(null); }} placeholder="ejemplo@gmail.com" className={`bg-transparent flex-1 text-sm font-bold outline-none ${themeClasses.textMain}`} required />
            </div>
          </div>
          <div>
            <label className={`${themeClasses.label} ${themeClasses.textMain}`}>Contraseña</label>
            <div className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl border ${themeClasses.inputBg} ${themeClasses.inputBorder}`}>
              <Lock size={16} className="text-slate-500" />
              <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Clave de acceso" className={`bg-transparent flex-1 text-sm font-bold outline-none ${themeClasses.textMain}`} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`${themeClasses.label} ${themeClasses.textMain}`}>Rol</label>
              <div className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl border ${themeClasses.inputBg} ${themeClasses.inputBorder}`}>
                <Shield size={16} className="text-slate-500" />
                <select value={role} onChange={(e) => setRole(e.target.value)} className={`bg-transparent flex-1 text-sm font-bold outline-none ${themeClasses.textMain} [&>option]:text-black w-full`}>
                  <option value="SUPERADMIN">SUPERADMIN</option>
                  <option value="ADMIN">ADMIN</option>
                  <option value="USUARIO">USUARIO</option>
                </select>
              </div>
            </div>
            <div>
              <label className={`${themeClasses.label} ${themeClasses.textMain}`}>Estado</label>
              <div className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl border ${themeClasses.inputBg} ${themeClasses.inputBorder}`}>
                <ToggleLeft size={16} className={status === 'ACTIVO' ? "text-emerald-500" : "text-slate-500"} />
                <select value={status} onChange={(e) => setStatus(e.target.value)} className={`bg-transparent flex-1 text-sm font-bold outline-none ${themeClasses.textMain} [&>option]:text-black w-full`}>
                  <option value="ACTIVO">ACTIVO</option>
                  <option value="INACTIVO">INACTIVO</option>
                </select>
              </div>
            </div>
          </div>
          <div>
            <label className={`${themeClasses.label} ${themeClasses.textMain}`}>Límite de Portafolios</label>
            <div className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl border ${themeClasses.inputBg} ${themeClasses.inputBorder}`}>
              <FolderPlus size={16} className="text-indigo-500" />
              <input 
                type="number" 
                min="1" 
                value={maxPortfolios} 
                onChange={(e) => setMaxPortfolios(parseInt(e.target.value) || 1)} 
                className={`bg-transparent flex-1 text-sm font-bold outline-none ${themeClasses.textMain}`} 
                required 
              />
            </div>
            <p className="text-[9px] text-slate-500 mt-1 pl-1">Cantidad máxima de portafolios que este usuario puede crear.</p>
          </div>
          <div>
            <label className={`${themeClasses.label} ${themeClasses.textMain}`}>Última Conexión (Auto)</label>
            <div className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl border ${themeClasses.inputBg} ${themeClasses.inputBorder} opacity-60`}>
              <Calendar size={16} className="text-slate-500" />
              <input type="text" value={date?.replace('T', ' ')} disabled className={`bg-transparent flex-1 text-xs font-mono outline-none ${themeClasses.textMain}`} />
            </div>
          </div>
          {error && (
            <div className="flex items-start space-x-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs font-bold animate-in fade-in zoom-in-95">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
            </div>
          )}
          <button type="submit" disabled={isSubmitting} className={`w-full py-3.5 mt-2 rounded-xl flex items-center justify-center space-x-2 font-black uppercase tracking-widest text-xs transition-all shadow-lg active:scale-95 disabled:opacity-50 ${theme === 'dark' ? 'bg-indigo-600 text-white hover:bg-indigo-500' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            <span>{isSubmitting ? 'Guardando...' : 'Guardar Usuario'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminUserModal;